import React, { useState, useEffect } from 'react';
import { Tabs, Button, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { StateService } from '../../services/api/stateService';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import JsonFieldEditor from '../common/JsonFieldEditor';
import { PropertyChangesEditor } from '../PrimeModel/PropertyChangesEditor';
import { ValidationRulesEditor } from '../Parameters/ValidationRulesEditor';
import type { TransitionParameters, ValidationRules, PropertyChange } from '../../types/transitionParameters';
import {
  CompanyArtifactId,
  CompanyStateId,
  CArtifact,
  CArtifactTransition,
  CreateCArtifactTransition,
} from '../../types/artifacts-branded';
import { ArtifactItemState, ArtifactItemTransition } from '../../types/artifactItems';
import styles from './TransitionForm.module.css';

interface TransitionFormData {
  name: string;
  description: string;
  from_state_ref: string;
  to_state_ref: string;
  default_transition_type: string;
  transition_conditions: Record<string, unknown>;
  transition_parameters: Record<string, unknown>;
  validation_rules: Record<string, unknown>;
  input_output_ratio: number;
  effective_start_date: string;
  effective_end_date: string;
  is_active: boolean;
  demand_type: string;
  demand_flow_type: string;
  is_improvement: boolean;
  improvement_impact: Record<string, unknown>;
  demand_calculation_rules: Record<string, unknown>;
}

interface UnifiedTransitionFormProps {
  onSubmit: (transition: CArtifactTransition | CreateCArtifactTransition) => Promise<void>;
  onCancel: () => void;
  initialTransition?: (CArtifactTransition | ArtifactItemTransition) | null;
  usePrime: boolean;
  artifactId: CompanyArtifactId;
  artifact?: CArtifact | null;
}

export const UnifiedTransitionForm: React.FC<UnifiedTransitionFormProps> = ({
  onSubmit,
  onCancel,
  initialTransition,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  usePrime,
  artifactId,
  artifact,
}) => {
  const [states, setStates] = useState<(ArtifactItemState & { isPrimeState: boolean })[]>([]);
  const [activeTab, setActiveTab] = useState('basic');
  const [artifactProperties, setArtifactProperties] = useState<any[]>([]);
  const [transitionParams, setTransitionParams] = useState<TransitionParameters>({});
  const [validationRules, setValidationRules] = useState<ValidationRules>({});
  const [formData, setFormData] = useState<TransitionFormData>({
    name: '',
    description: '',
    from_state_ref: '',
    to_state_ref: '',
    default_transition_type: 'PROCESSITEM',
    transition_conditions: {},
    transition_parameters: {},
    validation_rules: {},
    input_output_ratio: 1.0,
    effective_start_date: new Date().toISOString(),
    effective_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
    is_active: true,
    demand_type: 'TARGET',
    demand_flow_type: 'DRUM',
    is_improvement: false,
    improvement_impact: {},
    demand_calculation_rules: {},
  });

  // Load states for the form
  useEffect(() => {
    const loadStates = async () => {
      try {
        const allStates: (ArtifactItemState & { isPrimeState: boolean })[] = [];

        // Load regular states
        try {
          const regularStates = await StateService.getStatesByArtifact(artifactId);
          allStates.push(...regularStates.map((state) => ({ ...state, isPrimeState: false })));
        } catch (err) {
          console.warn('Failed to load regular states:', err);
        }

        // Load prime states if available
        const primeArtifactId = artifact?.prime_artifact_id;
        if (primeArtifactId) {
          try {
            const primeStates = await PrimeArtifactService.getPrimeArtifactStates(primeArtifactId);
            allStates.push(
              ...primeStates.map(
                (state) =>
                  ({
                    ...state,
                    artifact_ref: state.prime_artifact_ref,
                    artifact_id: state.prime_artifact_ref,
                    is_deleted: false,
                    isPrimeState: true,
                  }) as ArtifactItemState & { isPrimeState: boolean }
              )
            );
          } catch (err) {
            console.warn('Failed to load prime states:', err);
          }
        }

        setStates(allStates);
      } catch (error) {
        console.error('Error loading states:', error);
      }
    };
    void loadStates();
  }, [artifactId, artifact]);

  // Load artifact properties for property operations
  useEffect(() => {
    const loadProperties = async () => {
      try {
        // Load properties from the artifact
        // This would need to be implemented based on your API
        // For now, using empty array
        setArtifactProperties([]);
      } catch (error) {
        console.error('Error loading artifact properties:', error);
      }
    };
    if (artifactId) {
      void loadProperties();
    }
  }, [artifactId]);

  // Initialize form data
  useEffect(() => {
    if (initialTransition) {
      // Handle both CArtifactTransition and ArtifactItemTransition types
      const transition = initialTransition as ArtifactItemTransition;
      const params = (transition.transition_parameters || {}) as TransitionParameters;
      const rules = (transition.validation_rules || {}) as ValidationRules;
      const conditions = transition.transition_conditions || {};
      
      setTransitionParams(params);
      setValidationRules(rules);
      
      // Extract state IDs - handle both field name variations
      const fromStateId = (transition as any).from_state_id || transition.input_state_ref || '';
      const toStateId = (transition as any).to_state_id || transition.output_state_ref || '';
      
      setFormData({
        name: transition.name || '',
        description: transition.description || '',
        from_state_ref: fromStateId,
        to_state_ref: toStateId,
        default_transition_type: transition.transition_type || 'PROCESSITEM',
        transition_conditions: conditions as Record<string, unknown>,
        transition_parameters: params as Record<string, unknown>,
        validation_rules: rules as Record<string, unknown>,
        input_output_ratio: transition.input_output_ratio || 1.0,
        effective_start_date: transition.effective_start_date || new Date().toISOString(),
        effective_end_date: transition.effective_end_date || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        is_active: transition.is_active !== undefined ? transition.is_active : true,
        demand_type: transition.demand_type || 'TARGET',
        demand_flow_type: transition.demand_flow_type || 'DRUM',
        is_improvement: transition.is_improvement ?? false,
        improvement_impact: transition.improvement_impact || {},
        demand_calculation_rules: transition.demand_calculation_rules || {},
      });
    } else {
      setTransitionParams({});
      setValidationRules({});
    }
  }, [initialTransition]);

  // Handle copying from prime artifact transition
  const handleCopyFromPrime = async () => {
    if (!artifact?.prime_artifact_id) {
      message.warning('No prime artifact associated with this artifact');
      return;
    }

    try {
      // Find matching prime transition by name or state refs
      const primeTransitions = await PrimeArtifactService.getPrimeArtifactTransitions(artifact.prime_artifact_id);
      const matchingTransition = primeTransitions.find(
        (pt) => pt.name === formData.name || 
        (pt.from_state_ref === formData.from_state_ref && pt.to_state_ref === formData.to_state_ref)
      );

      if (!matchingTransition) {
        message.warning('No matching prime transition found');
        return;
      }

      // Copy property operations and validation rules
      const primeParams = (matchingTransition.transition_parameters || {}) as TransitionParameters;
      const primeRules = (matchingTransition.validation_rules || {}) as ValidationRules;

      setTransitionParams(primeParams);
      setValidationRules(primeRules);
      
      // Update form data
      setFormData(prev => ({
        ...prev,
        transition_parameters: primeParams as Record<string, unknown>,
        validation_rules: primeRules as Record<string, unknown>
      }));

      message.success('Copied property operations and validation rules from prime transition');
    } catch (error) {
      console.error('Error copying from prime:', error);
      message.error('Failed to copy from prime transition');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev: TransitionFormData) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleJsonChange = (field: string, value: Record<string, unknown>) => {
    setFormData((prev: TransitionFormData) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Include property operations and validation rules in transition_parameters
      const finalParams: TransitionParameters = {
        ...transitionParams,
        property_changes: transitionParams.property_changes || []
      };

      // Map field names to match backend expectations and remove invalid fields
      const mappedData = {
        artifact_id: artifactId,
        from_state_id: formData.from_state_ref as CompanyStateId,
        to_state_id: formData.to_state_ref as CompanyStateId,
        name: formData.name,
        description: formData.description,
        transition_parameters: finalParams,
        validation_rules: validationRules,
        transition_conditions: formData.transition_conditions,
        is_improvement: formData.is_improvement,
      };
      await onSubmit(mappedData);
    } catch (error) {
      console.error('Error saving transition:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.transitionForm}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'basic',
            label: 'Basic Info',
            children: (
              <>
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
        />
      </div>

      <div className="form-group">
        <label htmlFor="from_state_ref">From State</label>
        <select
          id="from_state_ref"
          name="from_state_ref"
          value={formData.from_state_ref}
          onChange={handleChange}
          required
        >
          <option value="">Select a state</option>
          {states.map((state) => (
            <option key={state.id} value={state.id}>
              {state.name} {state.isPrimeState && '(Prime)'}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="to_state_ref">To State</label>
        <select
          id="to_state_ref"
          name="to_state_ref"
          value={formData.to_state_ref}
          onChange={handleChange}
          required
        >
          <option value="">Select a state</option>
          {states.map((state) => (
            <option key={state.id} value={state.id}>
              {state.name} {state.isPrimeState && '(Prime)'}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="default_transition_type">Transition Type</label>
        <select
          id="default_transition_type"
          name="default_transition_type"
          value={formData.default_transition_type}
          onChange={handleChange}
          required
        >
          <option value="PROCESSITEM">Process Item</option>
          <option value="MATERIAL">Material</option>
          <option value="RESOURCE">Resource</option>
          <option value="PROCESSITEM_PERSISTENT">Process Item Persistent</option>
          <option value="CREATION">Creation</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="input_output_ratio">Input/Output Ratio</label>
        <input
          type="number"
          id="input_output_ratio"
          name="input_output_ratio"
          value={formData.input_output_ratio}
          onChange={handleChange}
          step="0.1"
          min="0"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="effective_start_date">Start Date</label>
        <input
          type="datetime-local"
          id="effective_start_date"
          name="effective_start_date"
          value={formData.effective_start_date?.split('.')[0]}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="effective_end_date">End Date</label>
        <input
          type="datetime-local"
          id="effective_end_date"
          name="effective_end_date"
          value={formData.effective_end_date?.split('.')[0]}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="demand_type">Demand Type</label>
        <select
          id="demand_type"
          name="demand_type"
          value={formData.demand_type}
          onChange={handleChange}
          required
        >
          <option value="TARGET">Target</option>
          <option value="CAPACITY">Capacity</option>
          <option value="SUPPLY">Supply</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="demand_flow_type">Demand Flow Type</label>
        <select
          id="demand_flow_type"
          name="demand_flow_type"
          value={formData.demand_flow_type}
          onChange={handleChange}
          required
        >
          <option value="DRUM">Drum</option>
          <option value="ROPE">Rope</option>
          <option value="BUFFER">Buffer</option>
        </select>
      </div>

      <div className="form-group">
        <label>
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={(e) =>
              setFormData((prev: TransitionFormData) => ({ ...prev, is_active: e.target.checked }))
            }
          />
          Active
        </label>
      </div>

      <div className="form-group">
        <label>
          <input
            type="checkbox"
            name="is_improvement"
            checked={formData.is_improvement}
            onChange={(e) =>
              setFormData((prev: TransitionFormData) => ({ ...prev, is_improvement: e.target.checked }))
            }
          />
          Is Improvement (separate from operational workflows)
        </label>
      </div>

                <div className="form-group">
                  <label>Transition Conditions</label>
                  <JsonFieldEditor
                    value={formData.transition_conditions}
                    onChange={(value: unknown) =>
                      handleJsonChange('transition_conditions', value as Record<string, unknown>)
                    }
                    template={[]}
                  />
                </div>
              </>
            )
          },
          {
            key: 'property-operations',
            label: 'Property Operations',
            children: (
              <div style={{ marginBottom: 16 }}>
                {artifact?.prime_artifact_id && (
                  <Button
                    icon={<CopyOutlined />}
                    onClick={handleCopyFromPrime}
                    style={{ marginBottom: 16 }}
                  >
                    Copy from Prime Transition
                  </Button>
                )}
                <PropertyChangesEditor
                  propertyChanges={transitionParams.property_changes || []}
                  onChange={(changes: PropertyChange[]) => {
                    setTransitionParams({
                      ...transitionParams,
                      property_changes: changes
                    });
                  }}
                  artifactProperties={artifactProperties}
                />
              </div>
            )
          },
          {
            key: 'validation-rules',
            label: 'Validation Rules',
            children: (
              <div style={{ marginBottom: 16 }}>
                {artifact?.prime_artifact_id && (
                  <Button
                    icon={<CopyOutlined />}
                    onClick={handleCopyFromPrime}
                    style={{ marginBottom: 16 }}
                  >
                    Copy from Prime Transition
                  </Button>
                )}
                <ValidationRulesEditor
                  validationRules={validationRules}
                  onChange={setValidationRules}
                  artifactProperties={artifactProperties}
                />
              </div>
            )
          }
        ]}
      />

      <div className={styles.formActions}>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit">Save</button>
      </div>
    </form>
  );
};
